import React from 'react'
import clsx from 'clsx'
import { Link } from '@src/components/Link'
import CaretRightIcon from '@icons/CaretRightIcon'
import CaretLeftIcon from '@icons/CaretLeftIcon'
import { Theme, List, ListItem } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { sendBreadcrumbsEvent } from '@src/utils/analytics'

const useStyles = makeStyles((theme: Theme) => {
  return {
    listRoot: {
      display: 'inline-flex',
      alignItems: 'center',
    },
    listItemRoot: {
      width: 'auto',
      padding: 0,
      display: 'none',
      fontSize: theme.typography.pxToRem(12),
      [theme.breakpoints.up('sm')]: {
        display: 'block',
      },
      '&$visibleOnMobile': {
        display: 'block',
      },
      '& sup': {
        verticalAlign: 'top',
      },
    },
    backIcon: {
      display: 'block',
      fontSize: theme.typography.pxToRem(12),
      marginRight: theme.spacing(2),
      marginLeft: theme.spacing(1),
      [theme.breakpoints.up('sm')]: {
        display: 'none',
      },
    },
    alwaysDisplayItem: {
      display: 'block',
    },
    dividerIcon: {
      fontSize: theme.typography.pxToRem(10),
      margin: theme.spacing(0, 2),
      display: 'none',
      [theme.breakpoints.up('sm')]: {
        display: 'inline-block',
      },
    },
    link: {
      display: 'flex',
      alignItems: 'center',
    },
    visibleOnMobile: {},
    '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
      dividerIcon: {
        width: 8,
      },
    },
  }
})

export interface BreadcrumbItem {
  url?: string
  label?: React.ReactNode
  gaLabel?: string
}

export interface BreadcrumbProps {
  items: BreadcrumbItem[]
  className?: string
  showAllLevels?: Boolean
  showBack?: Boolean
  eventLabel?: string
  alwaysDisplayCaretLeft?: Boolean
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({
  items,
  className,
  showAllLevels,
  showBack,
  alwaysDisplayCaretLeft,
}) => {
  const classes = useStyles()
  const listClasses = {
    root: clsx(classes.listRoot, className),
  }
  const listItemClasses = {
    root: classes.listItemRoot,
  }

  const eventLabel = items
    .filter((item) => item.gaLabel || item.label)
    .map((item) => item.gaLabel || item.label)

  return (
    <List classes={listClasses} disablePadding>
      {items.map(({ url, label, gaLabel }, index) => {
        // On mobile only show link to parent / show current page if only one breadcrumb item.
        const visibleOnMobile =
          index === items.length - 2 ||
          items.length === 1 ||
          (showAllLevels && index < items.length - 1)
        return (
          <React.Fragment key={index}>
            {index > 0 ? (
              <CaretRightIcon
                color="disabled"
                className={classes.dividerIcon}
              />
            ) : null}
            {url && !showAllLevels ? (
              <ListItem
                classes={listItemClasses}
                className={clsx(visibleOnMobile && classes.visibleOnMobile)}
              >
                <Link href={url} passHref>
                  <a
                    aria-hidden
                    className={classes.link}
                    onClick={() =>
                      sendBreadcrumbsEvent(
                        gaLabel || label,
                        eventLabel,
                        url,
                        showBack
                      )
                    }
                  >
                    {/* Icon only appears on mobile unless alwaysDisplayCaretLeft is true */}
                    <CaretLeftIcon
                      color="inherit"
                      className={clsx(
                        classes.backIcon,
                        alwaysDisplayCaretLeft
                          ? classes.alwaysDisplayItem
                          : null
                      )}
                    />
                    {label}
                  </a>
                </Link>
              </ListItem>
            ) : url && showAllLevels ? (
              <ListItem
                classes={listItemClasses}
                className={clsx(visibleOnMobile && classes.visibleOnMobile)}
              >
                <Link href={url} passHref>
                  <a
                    aria-hidden
                    className={classes.link}
                    onClick={() =>
                      sendBreadcrumbsEvent(
                        gaLabel || label,
                        eventLabel,
                        url,
                        showBack
                      )
                    }
                  >
                    {index !== 0 && (
                      <CaretRightIcon
                        color="disabled"
                        className={classes.backIcon}
                      />
                    )}
                    {label}
                  </a>
                </Link>
              </ListItem>
            ) : (
              <ListItem
                classes={listItemClasses}
                className={clsx(visibleOnMobile && classes.visibleOnMobile)}
              >
                {label}
              </ListItem>
            )}
          </React.Fragment>
        )
      })}
    </List>
  )
}

export default Breadcrumb
